import React, { useCallback, useEffect, useState } from "react";
import {
  getAuthenticatedAccount,
  initializeAuthentication,
  signIn,
  signOut,
} from '../config/auth';

/* ============================
   Entra External ID (CIAM)
   ============================ */

const SIGNUP_URL = "https://myresumes.org/enrollment.html";

/* ============================
   Hook
   ============================ */

function useCiamLogin() {
  function getAccount() {
    return getAuthenticatedAccount();
  }

  return { signIn, signOut, getAccount };
}

/* ============================
   Login Component (UPDATED UI)
   ============================ */

const Login = ({ setAuthenticated, setFormData }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { signIn: login } = useCiamLogin();

  const finishAuthentication = useCallback((account) => {
    if (!account) return;
    setAuthenticated(true);
    setFormData((prev) => ({
      ...prev,
      user: account.username,
    }));
  }, [setAuthenticated, setFormData]);

  useEffect(() => {
    let active = true;
    initializeAuthentication()
      .then(() => {
        if (active) finishAuthentication(getAuthenticatedAccount());
      })
      .catch(() => {
        if (active) setError('Unable to restore your sign-in session.');
      });
    return () => {
      active = false;
    };
  }, [finishAuthentication]);

  const handleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await login();
      const acct = result?.account;
      finishAuthentication(acct);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>
          Federal Employment Assistance
        </h1>

        <p style={styles.subtitle}>
          Paid resume-building and federal employment support tools.
        </p>

        <button
          style={styles.secondaryButton}
          onClick={() => window.location.assign(SIGNUP_URL)}
          type="button"
        >
          Sign Up
        </button>

        <button
          style={{
            ...styles.button,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onClick={handleLogin}
          disabled={loading}
          type="button"
        >
          {loading ? "Signing you in..." : "Sign in with Microsoft"}
        </button>

        {error && <p style={styles.error} role="alert">{error}</p>}

        <p style={styles.footer}>
          Secure authentication powered by Microsoft Entra
        </p>
      </div>
    </div>
  );
};

export default Login;
export { useCiamLogin };

/* ============================
   Styles
   ============================ */

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #0f172a, #1e293b)",
    padding: "1rem",
  },
  card: {
    background: "#ffffff",
    borderRadius: "14px",
    padding: "2.5rem",
    maxWidth: "440px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
  },
  title: {
    fontSize: "1.65rem",
    fontWeight: "700",
    marginBottom: "0.75rem",
    color: "#0f172a",
  },
  subtitle: {
    fontSize: "0.95rem",
    color: "#475569",
    marginBottom: "2rem",
    lineHeight: "1.5",
  },
  button: {
    width: "100%",
    padding: "0.85rem 1rem",
    fontSize: "1rem",
    fontWeight: "600",
    borderRadius: "8px",
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
  },
  secondaryButton: {
    width: "100%",
    padding: "0.85rem 1rem",
    fontSize: "1rem",
    fontWeight: "600",
    borderRadius: "8px",
    border: "1px solid #2563eb",
    background: "#ffffff",
    color: "#2563eb",
    marginBottom: "0.75rem",
    cursor: "pointer",
  },
  error: {
    marginTop: "1rem",
    color: "#dc2626",
    fontSize: "0.9rem",
  },
  footer: {
    marginTop: "2rem",
    fontSize: "0.8rem",
    color: "#64748b",
  },
};
